import { Col, Icon, Row, Select, Tabs } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Creators as ReportActions } from "../../../../store/ducks/report";
import DrawerFinancialCashFlowBudget from "../../../../components/drawers/financialCashFlowBudget";
import ButtonStandard from "../../../../components/utils/button";
import { useFinancial } from "../../../../hooks/useFinancialReducer";
import { findAll } from "../../../../services/financialProjectService";
import FinancialCashFlowExpense from "./expense";
import FinancialCashFlowIncome from "./income";
import StackedColumnBarChart from "./stackedColumnBarChart";
import { CardCustom, Title } from "./styles";
import SelectCurrency from "../../../../components/utils/selectCurrency";
import { useCurrencyContext } from "../../../../hooks/useCurrencyContext";
import { indexAllDropdownBankAccountFarm } from "../../../../services/bankAccountFarmService";
import { getParameterItems } from "../../../../services/generalParameterService";

// Services
const CODES_PARAMETERS = [4006];

const FinancialCashFlow = () => {
  const [tabSelected, setTabSelected] = useState("0");
  const [listFinancialProjects, setListFinancialProjects] = useState([]);
  const [isLoadingFinancialProjects, setIsLoadingFinancialProjects] =
    useState(false);
  const [selectedFinancialProject, setSelectedFinancialProject] =
    useState(null);
  const [selectedBankAccountFarmId, setSelectedBankAccountFarmId] =
    useState(null);
  const [bankAccountFarmOptions, setBankAccountFarmOptions] = useState([]);
  const [isLoadingBankAccountFarm, setIsLoadingBankAccountFarm] =
    useState(false);
  const [parametersValue, setParametersValue] = useState([]);
  // Redux
  const {
    refreshListFinancialCashFlow,
    openOrCloseDrawerFinancialCashFlowBudget,
    isLoadingListFinancialCashFlow: isLoading,
  } = useFinancial();
  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  const dispatch = useDispatch();

  const { defaultCurrencyCode } = useCurrencyContext();

  // Effects
  // Memo
  const isEnableBankAccountFarm = useMemo(() => {
    return (
      parametersValue.find((parameter) => parameter.code === 4006) &&
      parametersValue.find((parameter) => parameter.code === 4006).value ===
        "true"
    );
  }, [parametersValue]);
  // Methods
  const handleTabClick = (key, e) => {
    setTabSelected(key);
  };

  const handleShowModalCashFlowReport = () => {
    dispatch(ReportActions.showModal("FinancialCashFlowReport"));
  };

  const handleShowModalDisbursementReport = () => {
    dispatch(ReportActions.showModal("DisbursementReport"));
  };

  useEffect(() => {
    async function fetchProjects() {
      setIsLoadingFinancialProjects(true);
      try {
        const {
          data: { results },
        } = await findAll({
          groupId,
          farmId,
        });
        setListFinancialProjects(results);
      } catch (error) {
      } finally {
        setIsLoadingFinancialProjects(false);
      }
    }
    fetchProjects();
  }, [farmId, groupId]);

  useEffect(() => {
    async function getParameters() {
      let dataResult = [];
      try {
        for (var i = 0; i < CODES_PARAMETERS.length; i++) {
          let code = CODES_PARAMETERS[i];
          let {
            data: { results },
          } = await getParameterItems({ groupId, farmId, code });
          results.isDefault = results.farmId == null && results.groupId == null;
          results.updated = false;
          dataResult.push(results);
        }
        setParametersValue(dataResult);
      } catch (error) {
      } finally {
      }
    }
    async function fetchBankAccountFarmOptions() {
      if (isEnableBankAccountFarm && groupId && farmId) {
        setIsLoadingBankAccountFarm(true);
        try {
          const {
            data: { results },
          } = await indexAllDropdownBankAccountFarm({ groupId, farmId });
          setBankAccountFarmOptions(results || []);
        } catch (error) {
          setBankAccountFarmOptions([]);
        } finally {
          setIsLoadingBankAccountFarm(false);
        }
      }
    }
    function fetchAll() {
      Promise.all([getParameters(), fetchBankAccountFarmOptions()]);
    }
    fetchAll();
  }, [groupId, farmId, isEnableBankAccountFarm]);

  return (
    <CardCustom>
      <Row type="flex" className="rowHeader">
        <Col span={4}>
          <Title>{translation.financial.cashFlow.title}</Title>
        </Col>
        <Col span={20}>
          <Row type="flex" justify="end" align="middle" gutter={8}>
            <Col>
              <ButtonStandard
                buttonType="typeWithoutBackground"
                disabled={isLoading}
                onClick={() =>
                  refreshListFinancialCashFlow(
                    selectedFinancialProject,
                    selectedBankAccountFarmId
                  )
                }
              >
                <Icon type="reload" />
              </ButtonStandard>
            </Col>
            <Col>
              <ButtonStandard
                name="buttonCashFlowReport"
                buttonType="alternative"
                onClick={handleShowModalDisbursementReport}
              >
                {translation.financial.cashFlow.buttonDisbursementReport}
              </ButtonStandard>
            </Col>
            <Col>
              <ButtonStandard
                name="buttonCashFlowReport"
                buttonType="alternative"
                onClick={handleShowModalCashFlowReport}
              >
                {translation.financial.cashFlow.buttonCashFlowReport}
              </ButtonStandard>
            </Col>
            <Col>
              <ButtonStandard
                name="buttonCreateBudget"
                buttonType="addNew"
                onClick={openOrCloseDrawerFinancialCashFlowBudget}
              >
                {translation.financial.cashFlow.buttonCreateBudget}
              </ButtonStandard>
            </Col>
          </Row>
        </Col>
      </Row>
      <Row type="flex" justify="start" gutter={8} style={{ marginTop: "8px" }}>
        <Col>
          <Row>
            <label className="filterLabel">Moeda padrão</label>
          </Row>
          <Row>
            <SelectCurrency value={defaultCurrencyCode} disabled />
          </Row>
        </Col>
        <Col span={8}>
          <Row>
            <label className="filterLabel">
              {translation.financial.cashFlow.filters.financialProjectId}
            </label>
          </Row>
          <Row>
            <Select
              style={{ width: "100%" }}
              name="selectedFinancialProject"
              value={selectedFinancialProject || undefined}
              placeholder={translation.defaultSelectPlaceholder}
              loading={isLoadingFinancialProjects}
              allowClear
              onDeselect={() => {
                setSelectedFinancialProject(null);
                refreshListFinancialCashFlow(null, selectedBankAccountFarmId);
              }}
              onChange={(value) => {
                setSelectedFinancialProject(value);
                refreshListFinancialCashFlow(value, selectedBankAccountFarmId);
              }}
            >
              {listFinancialProjects.map((l) => (
                <Select.Option key={l.id} value={l.id}>
                  {l.name}
                </Select.Option>
              ))}
            </Select>
          </Row>
        </Col>
        <Col span={8}>
          <Row>
            <label className="filterLabel">
              {translation.financial.cashFlow.filters.bankAccountFarmId}
            </label>
          </Row>
          <Row>
            <Select
              style={{ width: "100%" }}
              name="selectedBankAccountFarmId"
              value={selectedBankAccountFarmId || undefined}
              placeholder={translation.defaultSelectPlaceholder}
              loading={isLoadingBankAccountFarm}
              allowClear
              onDeselect={() => {
                setSelectedBankAccountFarmId(null);
                refreshListFinancialCashFlow(selectedFinancialProject, null);
              }}
              onChange={(value) => {
                setSelectedBankAccountFarmId(value);
                refreshListFinancialCashFlow(selectedFinancialProject, value);
              }}
            >
              {bankAccountFarmOptions.map((bankAccountFarm) => (
                <Select.Option
                  key={bankAccountFarm.id}
                  value={bankAccountFarm.id}
                >
                  {bankAccountFarm.description}
                </Select.Option>
              ))}
            </Select>
          </Row>
        </Col>
      </Row>
      <Row type="flex">
        <Col span={24}>
          <StackedColumnBarChart />
        </Col>
      </Row>
      <Row type="flex">
        <Col span={24}>
          <Tabs
            type="card"
            defaultActiveKey="0"
            activeKey={tabSelected}
            onTabClick={handleTabClick}
          >
            <Tabs.TabPane tab={<span>Despesas</span>} key="0">
              <FinancialCashFlowExpense />
            </Tabs.TabPane>
            <Tabs.TabPane tab={<span>Receitas</span>} key="1">
              <FinancialCashFlowIncome />
            </Tabs.TabPane>
          </Tabs>
        </Col>
      </Row>
      <DrawerFinancialCashFlowBudget />
    </CardCustom>
  );
};

export default FinancialCashFlow;
